/* eslint-disable @typescript-eslint/no-unsafe-member-access,
                  @typescript-eslint/no-unsafe-call,
                  @typescript-eslint/no-explicit-any,
                  @typescript-eslint/no-unsafe-assignment */

import type { CSSProperties } from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import {
  defineMessages,
  FormattedDate,
  FormattedMessage,
  useIntl,
} from 'react-intl';

import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { useSelector } from 'react-redux';

import { debounce } from 'lodash';

import { apiGetFavouritedBy } from 'mastodon/api/status';
import { ContentWarning } from 'mastodon/components/content_warning';
import type { StatusLike } from 'mastodon/components/hashtag_bar';
import { FollowButton } from 'mastodon/components/follow_button';
import { getHashtagBarForStatus } from 'mastodon/components/hashtag_bar';
import { Icon } from 'mastodon/components/icon';
import { IconLogo } from 'mastodon/components/logo';
import PictureInPicturePlaceholder from 'mastodon/components/picture_in_picture_placeholder';
import { VisibilityIcon } from 'mastodon/components/visibility_icon';
import DropdownMenuContainer from 'mastodon/containers/dropdown_menu_container';
import { useIdentity } from 'mastodon/identity_context';
import { me } from 'mastodon/initial_state';
import {
  PERMISSION_MANAGE_FEDERATION,
  PERMISSION_MANAGE_USERS,
} from 'mastodon/permissions';

import { Avatar } from '../../../components/avatar';
import { DisplayName } from '../../../components/display_name';
import MediaGallery from '../../../components/media_gallery';
import StatusContent from '../../../components/status_content';
import Audio from '../../audio';
import scheduleIdleTask from '../../ui/util/schedule_idle_task';
import Video from '../../video';
import { BackButton } from 'mastodon/components/back_button';

import Card from './card';
import FavouritesTooltip from './favourites_tooltip';
import { useAppDispatch } from 'mastodon/store';
import { openModal } from 'mastodon/actions/modal';
import { showAlert } from 'mastodon/actions/alerts';
import { MoreHorizIcon } from 'mastodon/components/icons/more_horiz';
import { MentionIcon } from 'mastodon/components/icons/mention';

interface VideoModalOptions {
  startTime: number;
  autoPlay?: boolean;
  defaultVolume: number;
  componentIndex: number;
}

const messages = defineMessages({
  delete: { id: 'status.delete', defaultMessage: 'Delete' },
  redraft: { id: 'status.redraft', defaultMessage: 'Delete & re-draft' },
  edit: { id: 'status.edit', defaultMessage: 'Edit' },
  direct: { id: 'status.direct', defaultMessage: 'Privately mention {name}' },
  mention: { id: 'status.mention', defaultMessage: 'Mention {name}' },
  reply: { id: 'status.reply', defaultMessage: 'Reply' },
  reblog: { id: 'status.reblog', defaultMessage: 'Boost' },
  reblog_private: {
    id: 'status.reblog_private',
    defaultMessage: 'Boost with original visibility',
  },
  cancel_reblog_private: {
    id: 'status.cancel_reblog_private',
    defaultMessage: 'Unboost',
  },
  cannot_reblog: {
    id: 'status.cannot_reblog',
    defaultMessage: 'This post cannot be boosted',
  },
  favourite: { id: 'status.favourite', defaultMessage: 'Favorite' },
  removeFavourite: {
    id: 'status.remove_favourite',
    defaultMessage: 'Remove from favorites',
  },
  bookmark: { id: 'status.bookmark', defaultMessage: 'Bookmark' },
  removeBookmark: {
    id: 'status.remove_bookmark',
    defaultMessage: 'Remove bookmark',
  },
  more: { id: 'status.more', defaultMessage: 'More' },
  mute: { id: 'status.mute', defaultMessage: 'Mute {name}' },
  muteConversation: {
    id: 'status.mute_conversation',
    defaultMessage: 'Mute conversation',
  },
  unmuteConversation: {
    id: 'status.unmute_conversation',
    defaultMessage: 'Unmute conversation',
  },
  block: { id: 'status.block', defaultMessage: 'Block {name}' },
  report: { id: 'status.report', defaultMessage: 'Report {name}' },
  share_link: { id: 'status.share_link', defaultMessage: 'Share via link' },
  pin: { id: 'status.pin', defaultMessage: 'Pin on profile' },
  unpin: { id: 'status.unpin', defaultMessage: 'Unpin from profile' },
  embed: { id: 'status.embed', defaultMessage: 'Get embed code' },
  admin_account: {
    id: 'status.admin_account',
    defaultMessage: 'Open moderation interface for {name}',
  },
  admin_status: {
    id: 'status.admin_status',
    defaultMessage: 'Open this post in the moderation interface',
  },
  admin_domain: {
    id: 'status.admin_domain',
    defaultMessage: 'Open moderation interface for {domain}',
  },
  copy: { id: 'status.copy', defaultMessage: 'Copy link to post' },
  blockDomain: {
    id: 'account.block_domain',
    defaultMessage: 'Block domain {domain}',
  },
  unblockDomain: {
    id: 'account.unblock_domain',
    defaultMessage: 'Unblock domain {domain}',
  },
  unmute: { id: 'account.unmute', defaultMessage: 'Unmute {name}' },
  unblock: { id: 'account.unblock', defaultMessage: 'Unblock {name}' },
  openOriginalPage: {
    id: 'account.open_original_page',
    defaultMessage: 'Open original page',
  },
  likes: {
    id: 'status.like',
    defaultMessage: 'Like'
  },
  shares: {
    id: 'status.share',
    defaultMessage: 'Shares'
  },
  comments: {
    id: 'status.comment',
    defaultMessage: 'Comments'
  },
  back: { id: 'status.back', defaultMessage: 'Back' },
  view: { id: 'status.view', defaultMessage: 'View' },
  copy_success: { id: 'status.copy_success', defaultMessage: 'Copy link to post successfully' },
});

const reconcileWithFavouritedState = (accounts: any[], isFavourited: boolean, currentUserAccount?: any): any[] => {
  const hasCurrentUser = accounts.some((a: any) => a.id === me);

  if (!isFavourited && hasCurrentUser) {
    return accounts.filter((a: any) => a.id !== me);
  }

  if (isFavourited && !hasCurrentUser && currentUserAccount) {
    return [currentUserAccount, ...accounts];
  }

  return accounts;
};

export const handleFavouriteTooltipClick = (
  status: any,
  dispatch: ReturnType<typeof useAppDispatch>,
  favouritesCache: React.MutableRefObject<Record<string, any[]>>,
  setFavourites: React.Dispatch<React.SetStateAction<any[]>>,
  setIsFetchingFavourites: React.Dispatch<React.SetStateAction<boolean>>,
  currentUserAccount?: any,
) => {
  const statusId = status.get('id');
  const isFavourited = status.get('favourited') as boolean;
  const totalCount = status.get('favourites_count') as number;

  if (favouritesCache.current[statusId]) {
    const reconciled = reconcileWithFavouritedState(favouritesCache.current[statusId], isFavourited, currentUserAccount);
    dispatch(
      openModal({
        modalType: 'FAVOURITES',
        modalProps: {
          accounts: reconciled,
          statusId,
          totalCount,
        },
      }),
    );
  } else {
    setIsFetchingFavourites(true);
    apiGetFavouritedBy(statusId)
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format from apiGetFavouritedBy');
        }
        const reconciled = reconcileWithFavouritedState(data, isFavourited, currentUserAccount);
        favouritesCache.current[statusId] = reconciled;
        setFavourites(reconciled);
        setIsFetchingFavourites(false);
        dispatch(
          openModal({
            modalType: 'FAVOURITES',
            modalProps: {
              accounts: reconciled,
              statusId,
              totalCount,
            },
          }),
        );
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch favourites:', error);
          setFavourites([]);
          setIsFetchingFavourites(false);
        }
      });
  }
};


export const DetailedStatus: React.FC<{
  childRepliesSize: number;
  status: any;
  onOpenMedia?: (status: any, index: number, lang: string) => void;
  onOpenVideo?: (status: any, lang: string, options: VideoModalOptions) => void;
  onTranslate?: (status: any) => void;
  measureHeight?: boolean;
  onHeightChange?: () => void;
  domain: string;
  showMedia?: boolean;
  withLogo?: boolean;
  overrideDisplayName?: React.ReactNode;
  pictureInPicture: any;
  onToggleHidden?: (status: any) => void;
  onToggleMediaVisibility?: () => void;
  onDelete: (status: any, withRedraft: boolean) => void;
  onEdit: (status: any) => void;
  onDirect: (account: any) => void;
  onMention: (account: any) => void;
  onMute: (account: any) => void;
  onUnmute: (account: any) => void;
  onMuteConversation: (status: any) => void;
  onBlock: (status: any) => void;
  onUnblock: (account: any) => void;
  onBlockDomain: (account: any) => void;
  onUnblockDomain: (domain: any) => void;
  onReport: (status: any) => void;
  onPin: (status: any) => void;
  onEmbed: (status: any) => void;
  onBookmark: (status: any) => void;
}> = ({
  status,
  onOpenMedia,
  onOpenVideo,
  onTranslate,
  measureHeight,
  onHeightChange,
  domain,
  showMedia,
  withLogo,
  overrideDisplayName,
  pictureInPicture,
  onToggleMediaVisibility,
  onToggleHidden,
  onBookmark,
  onDelete,
  onEdit,
  onMention,
  onMute,
  onUnmute,
  onMuteConversation,
  onBlock,
  onUnblock,
  onBlockDomain,
  onUnblockDomain,
  onReport,
  onPin,
  onEmbed,
  childRepliesSize
}) => {
    const { signedIn, permissions } = useIdentity();
    const relationship = useSelector((state: any) =>
      state.getIn(['relationships', status.getIn(['account', 'id'])]),
    );
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const publicStatus = ['public', 'unlisted'].includes(
      status.get('visibility') as string,
    );
    const pinnableStatus = ['public', 'unlisted', 'private'].includes(
      status.get('visibility') as string,
    );
    const mutingConversation = status.get('muted');
    const account = status.get('account');
    const writtenByMe = status.getIn(['account', 'id']) === me;
    const pinnedRebloggedByMe = status.get('pinned') && status.get('reblogged');
    const canPinStatus = writtenByMe && pinnableStatus && !status.get('pinned');
    const canUnpinStatus = (writtenByMe || pinnedRebloggedByMe) && status.get('pinned');
    const isRemote =
      status.getIn(['account', 'username']) !== status.getIn(['account', 'acct']);

    const [showFavouritesTooltip, setShowFavouritesTooltip] = useState(false);
    const [favourites, setFavourites] = useState([]);
    const [isFetchingFavourites, setIsFetchingFavourites] = useState(false);
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);
    const abortController = useRef(new AbortController());

    const favouritesCache = useRef<any>({});
    const isFetchingRef = useRef(false);

    const isMobile = useCallback(() => {
      return window.innerWidth < 630;
    }, []);

    const hideTooltip = useCallback(
      debounce(() => {
        setShowFavouritesTooltip(false);
        setIsTooltipHovered(false);
      }, 300,
        { leading: false, trailing: true },
      ),
      [],
    );

    const fetchFavourites = useCallback(
      debounce(async () => {
        const statusId = status.get('id');
        if (favouritesCache.current[statusId]) {
          setFavourites(favouritesCache.current[statusId]);
          if (!isMobile()) {
            setShowFavouritesTooltip(true);
          }
          setShowFavouritesTooltip(true);
          return;
        }
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        abortController.current.abort();
        abortController.current = new AbortController();

        setIsFetchingFavourites(true);
        try {
          const data = await apiGetFavouritedBy(statusId);
          favouritesCache.current[statusId] = data;
          setFavourites(data);
          setIsFetchingFavourites(false);
          if (data && data.length > 0 && !isMobile()) {
            setShowFavouritesTooltip(true);
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Failed to fetch favourites:', error);
            setFavourites([]);
            setIsFetchingFavourites(false);
          }
        } finally {
          isFetchingRef.current = false;
        }
      }, 500, { leading: true, trailing: false }),
      [status, isMobile]
    );

    const handleFavouriteMouseEnter = useCallback(() => {
      if (isMobile()) return;

      setShowFavouritesTooltip(true);
      void fetchFavourites();
      hideTooltip.cancel();
    }, [fetchFavourites, hideTooltip, isMobile]);

    const handleFavouriteMouseLeave = useCallback(() => {
      if (!isTooltipHovered) {
        hideTooltip();
      }
    }, [hideTooltip, isTooltipHovered]);

    const handleTooltipMouseEnter = useCallback(() => {
      if (isMobile()) return;

      setIsTooltipHovered(true);
      hideTooltip.cancel();
    }, [hideTooltip, isMobile]);

    const handleTooltipMouseLeave = useCallback(() => {
      if (isMobile()) return;

      setIsTooltipHovered(false);
      hideTooltip();
    }, [hideTooltip, isMobile]);

    useEffect(() => {
      return () => {
        abortController.current.abort();
        hideTooltip.cancel();
        fetchFavourites.cancel();
      };
    }, [hideTooltip, fetchFavourites]);


    useEffect(() => {
      const statusId = status.get('id');
      if (favouritesCache.current[statusId]) {
        delete favouritesCache.current[statusId];
        setFavourites([]);
        if (showFavouritesTooltip && !isMobile()) {
          void fetchFavourites();
        }
      }
    }, [status.get('favourites_count'), showFavouritesTooltip, isMobile, fetchFavourites]);

    const handleDeleteClick = () => {
      onDelete(status, false);
    };

    const handleEditClick = () => {
      onEdit(status);
    };

    const handleBookmarkClick = () => {
      onBookmark(status);
    };

    const handleMentionClick = () => {
      onMention(status.get('account'));
    };

    const handleMuteClick = () => {
      const account = status.get('account');

      if (relationship?.get('muting')) {
        onUnmute(account);
      } else {
        onMute(account);
      }
    };

    const handleBlockClick = () => {
      const account = status.get('account');

      if (relationship?.get('blocking')) {
        onUnblock(account);
      } else {
        onBlock(status);
      }
    };

    const handleBlockDomain = () => {
      const account = status.get('account');
      onBlockDomain(account);
    };

    const handleUnblockDomain = () => {
      const account = status.get('account');
      onUnblockDomain(account.get('acct').split('@')[1]);
    };

    const handleConversationMuteClick = () => {
      onMuteConversation(status);
    };

    const handleReport = () => {
      onReport(status);
    };

    const handlePinClick = () => {
      onPin(status);
    };

    const handleShare = async () => {
      await navigator.share({
        url: status.get('url'),
      });
    };

    const handleEmbed = () => {
      onEmbed(status);
    };

    const handleCopy = async () => {
      const url: string = status.get('url');
      await navigator.clipboard.writeText(url);
      dispatch(showAlert({ message: messages.copy_success }));
    };
    const menu = [];

    if (publicStatus && isRemote) {
      menu.push({
        text: intl.formatMessage(messages.openOriginalPage),
        href: status.get('url'),
      });
    }

    menu.push({ text: intl.formatMessage(messages.copy), action: handleCopy });

    if (publicStatus && 'share' in navigator && !status.get('in_reply_to_id')) {
      menu.push({
        text: intl.formatMessage(messages.share_link),
        action: handleShare,
      });
    }

    if (signedIn) {
      menu.push(null);

      if (writtenByMe || canUnpinStatus) {
        if (canPinStatus || canUnpinStatus) {
          menu.push({
            text: intl.formatMessage(
              status.get('pinned') ? messages.unpin : messages.pin,
            ),
            action: handlePinClick,
          });
          menu.push(null);
        }
      }

      if (writtenByMe) {
        menu.push({
          text: intl.formatMessage(
            mutingConversation
              ? messages.unmuteConversation
              : messages.muteConversation,
          ),
          action: handleConversationMuteClick,
        });
        menu.push(null);
        menu.push({
          text: intl.formatMessage(messages.edit),
          action: handleEditClick,
        });
        menu.push({
          text: intl.formatMessage(messages.delete),
          action: handleDeleteClick,
          dangerous: true,
        });
        // menu.push({ text: intl.formatMessage(messages.redraft), action: this.handleRedraftClick, dangerous: true });
      } else {
        menu.push({
          text: intl.formatMessage(messages.mention, {
            name: status.getIn(['account', 'display_name']),
          }),
          action: handleMentionClick,
        });
        menu.push(null);

        if (relationship?.get('muting')) {
          menu.push({
            text: intl.formatMessage(messages.unmute, {
              name: account.get('display_name'),
            }),
            action: handleMuteClick,
          });
        } else {
          menu.push({
            text: intl.formatMessage(messages.mute, {
              name: account.get('display_name'),
            }),
            action: handleMuteClick,
            dangerous: true,
          });
        }

        if (relationship?.get('blocking')) {
          menu.push({
            text: intl.formatMessage(messages.unblock, {
              name: account.get('display_name'),
            }),
            action: handleBlockClick,
          });
        } else {
          menu.push({
            text: intl.formatMessage(messages.block, {
              name: account.get('display_name'),
            }),
            action: handleBlockClick,
            dangerous: true,
          });
        }

        menu.push({
          text: intl.formatMessage(messages.report, {
            name: account.get('display_name'),
          }),
          action: handleReport,
          dangerous: true,
        });

        if (account.get('acct') !== account.get('username')) {
          const domain = account.get('acct').split('@')[1];

          menu.push(null);

          if (relationship?.get('domain_blocking')) {
            menu.push({
              text: intl.formatMessage(messages.unblockDomain, { domain }),
              action: handleUnblockDomain,
            });
          } else {
            menu.push({
              text: intl.formatMessage(messages.blockDomain, { domain }),
              action: handleBlockDomain,
              dangerous: true,
            });
          }
        }

        if (
          (permissions & PERMISSION_MANAGE_USERS) === PERMISSION_MANAGE_USERS ||
          (isRemote &&
            (permissions & PERMISSION_MANAGE_FEDERATION) ===
            PERMISSION_MANAGE_FEDERATION)
        ) {
          menu.push(null);

          if (
            isRemote &&
            (permissions & PERMISSION_MANAGE_FEDERATION) ===
            PERMISSION_MANAGE_FEDERATION
          ) {
            const domain = account.get('acct').split('@')[1];
            menu.push({
              text: intl.formatMessage(messages.admin_domain, { domain: domain }),
              href: `/admin/instances/${domain}`,
            });
          }
        }
      }
      menu.push({
        text: status.get('bookmarked') ? intl.formatMessage(messages.removeBookmark) : intl.formatMessage(messages.bookmark),
        action: handleBookmarkClick
      });
    }

    const isReblog = Boolean(status.get('reblog'));
    const actualStatus = isReblog ? status.get('reblog') : status;
    const reblogCaption = status.get('reblog_caption') as string | undefined;
    const actualStatusCreatedAt = actualStatus.get('created_at') as string;
    const [height, setHeight] = useState(0);
    const nodeRef = useRef<HTMLDivElement>();

    const handleOpenVideo = useCallback(
      (options: VideoModalOptions) => {
        const lang = (actualStatus.getIn(['translation', 'language']) ||
          actualStatus.get('language')) as string;
        if (onOpenVideo)
          onOpenVideo(actualStatus.getIn(['media_attachments', 0]), lang, options);
      },
      [actualStatus, onOpenVideo],
    );

    const _measureHeight = useCallback(
      (heightJustChanged?: boolean) => {
        if (measureHeight && nodeRef.current) {
          scheduleIdleTask(() => {
            if (nodeRef.current)
              setHeight(Math.ceil(nodeRef.current.scrollHeight) + 1);
          });

          if (onHeightChange && heightJustChanged) {
            onHeightChange();
          }
        }
      },
      [onHeightChange, measureHeight, setHeight],
    );

    const handleRef = useCallback(
      (c: HTMLDivElement) => {
        nodeRef.current = c;
        _measureHeight();
      },
      [_measureHeight],
    );

    const handleTranslate = useCallback(() => {
      if (onTranslate) onTranslate(status);
    }, [onTranslate, status]);

    const handleExpandedToggle = useCallback(() => {
      if (onToggleHidden) onToggleHidden(actualStatus);
    }, [actualStatus, onToggleHidden]);

    if (!actualStatus) {
      return null;
    }

    let media;
    // let applicationLink;
    // let reblogLink;
    let attachmentAspectRatio;

    if (actualStatus.get('media_attachments').getIn([0, 'type']) === 'video') {
      attachmentAspectRatio = `${actualStatus.get('media_attachments').getIn([0, 'meta', 'original', 'width'])} / ${actualStatus.get('media_attachments').getIn([0, 'meta', 'original', 'height'])}`;
    } else if (
      actualStatus.get('media_attachments').getIn([0, 'type']) === 'audio'
    ) {
      attachmentAspectRatio = '16 / 9';
    } else {
      attachmentAspectRatio =
        actualStatus.get('media_attachments').size === 1 &&
          actualStatus
            .get('media_attachments')
            .getIn([0, 'meta', 'small', 'aspect'])
          ? actualStatus
            .get('media_attachments')
            .getIn([0, 'meta', 'small', 'aspect'])
          : '3 / 2';
    }

    const outerStyle = { boxSizing: 'border-box' } as CSSProperties;

    if (measureHeight) {
      outerStyle.height = height;
    }

    const language =
      actualStatus.getIn(['translation', 'language']) || actualStatus.get('language');

    if (pictureInPicture.get('inUse')) {
      media = <PictureInPicturePlaceholder aspectRatio={attachmentAspectRatio} />;
    } else if (actualStatus.get('media_attachments').size > 0) {
      if (
        ['image', 'gifv', 'unknown'].includes(
          actualStatus.getIn(['media_attachments', 0, 'type']) as string,
        ) ||
        actualStatus.get('media_attachments').size > 1
      ) {
        media = (
          <MediaGallery
            standalone
            sensitive={actualStatus.get('sensitive')}
            media={actualStatus.get('media_attachments')}
            lang={language}
            height={300}
            onOpenMedia={onOpenMedia}
            visible={showMedia}
            onToggleVisibility={onToggleMediaVisibility}
            statusId={actualStatus.get('id')}
            accountId={actualStatus.getIn(['account', 'id'])}
          />
        );
      } else if (actualStatus.getIn(['media_attachments', 0, 'type']) === 'audio') {
        const attachment = actualStatus.getIn(['media_attachments', 0]);
        const description =
          attachment.getIn(['translation', 'description']) ||
          attachment.get('description');

        media = (
          <Audio
            src={attachment.get('url')}
            alt={description}
            lang={language}
            duration={attachment.getIn(['meta', 'original', 'duration'], 0)}
            poster={
              attachment.get('preview_url') ||
              actualStatus.getIn(['account', 'avatar_static'])
            }
            backgroundColor={attachment.getIn(['meta', 'colors', 'background'])}
            foregroundColor={attachment.getIn(['meta', 'colors', 'foreground'])}
            accentColor={attachment.getIn(['meta', 'colors', 'accent'])}
            sensitive={actualStatus.get('sensitive')}
            visible={showMedia}
            blurhash={attachment.get('blurhash')}
            height={150}
            onToggleVisibility={onToggleMediaVisibility}
          />
        );
      } else if (actualStatus.getIn(['media_attachments', 0, 'type']) === 'video') {
        const attachment = actualStatus.getIn(['media_attachments', 0]);
        const description =
          attachment.getIn(['translation', 'description']) ||
          attachment.get('description');

        media = (
          <Video
            preview={attachment.get('preview_url')}
            frameRate={attachment.getIn(['meta', 'original', 'frame_rate'])}
            aspectRatio={`${attachment.getIn(['meta', 'original', 'width'])} / ${attachment.getIn(['meta', 'original', 'height'])}`}
            blurhash={attachment.get('blurhash')}
            src={attachment.get('url')}
            alt={description}
            lang={language}
            width={300}
            height={150}
            onOpenVideo={handleOpenVideo}
            sensitive={actualStatus.get('sensitive')}
            visible={showMedia}
            onToggleVisibility={onToggleMediaVisibility}
            statusId={actualStatus.get('id')}
            accountId={actualStatus.getIn(['account', 'id'])}
          />
        );
      }
    } else if (actualStatus.get('card')) {
      media = (
        <Card
          sensitive={actualStatus.get('sensitive')}
          onOpenMedia={onOpenMedia}
          card={actualStatus.get('card')}
        />
      );
    }

    // if (status.get('application')) {
    //   applicationLink = (
    //     <>
    //       ·
    //       <a
    //         className='detailed-status__application'
    //         href={status.getIn(['application', 'website'])}
    //         target='_blank'
    //         rel='noopener noreferrer'
    //       >
    //         {status.getIn(['application', 'name'])}
    //       </a>
    //     </>
    //   );
    // }
    //
    const visibilityLink = (
      <VisibilityIcon visibility={status.get('visibility')} />
    );
    //
    // if (['private', 'direct'].includes(status.get('visibility') as string)) {
    //   reblogLink = '';
    // } else {
    //   reblogLink = (
    //     <Link
    //       to={`/@${status.getIn(['account', 'acct'])}/${status.get('id')}/reblogs`}
    //       className='detailed-status__link'
    //     >
    //       <span className='detailed-status__reblogs'>
    //         <AnimatedNumber value={status.get('reblogs_count')} />
    //       </span>
    //       <FormattedMessage
    //         id='status.reblogs'
    //         defaultMessage='{count, plural, one {boost} other {boosts}}'
    //         values={{ count: status.get('reblogs_count') }}
    //       />
    //     </Link>
    //   );
    // }
    //
    // const favouriteLink = (
    //   <Link
    //     to={`/@${status.getIn(['account', 'acct'])}/${status.get('id')}/favourites`}
    //     className='detailed-status__link'
    //   >
    //     <span className='detailed-status__favorites'>
    //       <AnimatedNumber value={status.get('favourites_count')} />
    //     </span>
    //     <FormattedMessage
    //       id='status.favourites'
    //       defaultMessage='{count, plural, one {favorite} other {favorites}}'
    //       values={{ count: status.get('favourites_count') }}
    //     />
    //   </Link>
    // );

    const { statusContentProps, hashtagBar } = getHashtagBarForStatus(
      actualStatus as StatusLike,
    );
    const expanded =
      !actualStatus.get('hidden') || actualStatus.get('spoiler_text').length === 0;

    const headerContent = (
      <div
        className={`detailed-status__display-name ${window.innerWidth <= 630 && 'detailed-status__display-name-special'}`}
      >
        {window.innerWidth <= 630 && <BackButton style='header-back-button' onlyIcon={true} />}

        <Link
          to={`/@${status.getIn(['account', 'acct'])}`}
          data-hover-card-account={status.getIn(['account', 'id'])}
          className="detailed-status__display-avatar text-decoration-none"
        >
          <Avatar account={status.get('account')} size={46} />
        </Link>

        {overrideDisplayName ?? (
          <div className="detailed-status__info">
            <div className="detailed-status__follow_wrapper">
              <Link
                to={`/@${status.getIn(['account', 'acct'])}`}
                data-hover-card-account={status.getIn(['account', 'id'])}
                className="text-decoration-none"
              >
                <DisplayName account={status.get('account')} localDomain={domain} />
              </Link>
              {status && status.getIn(['account', 'id']) !== me && (
                <FollowButton accountId={status.getIn(['account', 'id'])} onlyFollow />
              )}
            </div>
            <div className="detailed__interaction-bar">
              {!status.get('in_reply_to_id') && (
                <div className="detailed-status__datetime" rel="noopener noreferrer">
                  <FormattedDate
                    value={new Date(status.get('created_at'))}
                    year="numeric"
                    month="short"
                    day="2-digit"
                    hour="2-digit"
                    minute="2-digit"
                  />
                </div>
              )}
              <span className="dot-separator-tiny">•</span>
              <div className="status__visibility-icon">{visibilityLink}</div>
            </div>
          </div>
        )}

        <div className="detailed-status__action-bar-dropdown">
          <DropdownMenuContainer
            icon="ellipsis-h"
            iconComponent={MoreHorizIcon}
            status={status}
            items={menu}
            direction="left"
            title={intl.formatMessage({ id: 'status.more', defaultMessage: 'More' })}
          />
        </div>

        {withLogo && (
          <>
            <div className="spacer" />
            <IconLogo />
          </>
        )}
      </div>
    );

    return (
      <div style={outerStyle}>
        <div ref={handleRef} className={classNames('detailed-status')}>
          {status.get('visibility') === 'direct' && (
            <div className='status__prepend'>
              <div className='status__prepend-icon-wrapper'>
                <Icon
                  id='at'
                  icon={MentionIcon}
                  className='status__prepend-icon'
                />
              </div>
              <FormattedMessage
                id='status.direct_indicator'
                defaultMessage='Private mention'
              />
            </div>
          )}

          {window.innerWidth <= 630 && document.body ? createPortal(headerContent, document.body) : headerContent}

          {isReblog && reblogCaption && (
            <div className='detailed-status__reblog-caption'>
              {reblogCaption}
            </div>
          )}

          {isReblog ? (
            <div className='detailed-status__shared-card'>
              <div className='detailed-status__shared-byline'>
                <Link
                  to={`/@${actualStatus.getIn(['account', 'acct'])}`}
                  data-hover-card-account={actualStatus.getIn(['account', 'id'])}
                  className='detailed-status__shared-byline__avatar text-decoration-none'
                >
                  <Avatar account={actualStatus.get('account')} size={36} />
                </Link>

                <div className='detailed-status__shared-byline__info'>
                  <div className='detailed-status__shared-byline__name status_account_and_time_and_follow_container'>
                    <Link
                      to={`/@${actualStatus.getIn(['account', 'acct'])}`}
                      data-hover-card-account={actualStatus.getIn(['account', 'id'])}
                      className='text-decoration-none'
                    >
                      <DisplayName account={actualStatus.get('account')} localDomain={domain} />
                    </Link>
                    {actualStatus.getIn(['account', 'id']) !== me && (
                      <FollowButton accountId={actualStatus.getIn(['account', 'id'])} onlyFollow />
                    )}
                  </div>

                  <div className='detailed-status__shared-byline__meta'>
                    <FormattedDate
                      value={new Date(actualStatusCreatedAt)}
                      year='numeric'
                      month='short'
                      day='2-digit'
                      hour='2-digit'
                      minute='2-digit'
                    />
                    <span className='dot-separator-tiny'>·</span>
                    <div className='status__visibility-icon'>
                      <VisibilityIcon visibility={actualStatus.get('visibility')} />
                    </div>
                  </div>
                </div>
              </div>

              {actualStatus.get('spoiler_text').length > 0 && (
                <ContentWarning
                  text={
                    actualStatus.getIn(['translation', 'spoilerHtml']) ||
                    actualStatus.get('spoilerHtml')
                  }
                  expanded={expanded}
                  onClick={handleExpandedToggle}
                />
              )}

              {expanded && (
                <>
                  <StatusContent
                    status={actualStatus}
                    onTranslate={handleTranslate}
                    {...(statusContentProps as any)}
                  />

                  {hashtagBar}
                  {media}
                </>
              )}
            </div>
          ) : (
            <>
              {actualStatus.get('spoiler_text').length > 0 && (
                <ContentWarning
                  text={
                    actualStatus.getIn(['translation', 'spoilerHtml']) ||
                    actualStatus.get('spoilerHtml')
                  }
                  expanded={expanded}
                  onClick={handleExpandedToggle}
                />
              )}

              {expanded && (
                <>
                  <StatusContent
                    status={actualStatus}
                    onTranslate={handleTranslate}
                    {...(statusContentProps as any)}
                  />

                  {hashtagBar}
                  {media}
                </>
              )}
            </>
          )}
        </div>
      </div>
    );
  };
